import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, ChevronDown, ChevronUp, MessageSquare } from 'lucide-react'
import { toast } from '@/components/ui/sonner'
import { track } from '@adthlete/shared-frontend'
import athleteApi from '@/lib/api'
import { cn } from '@/lib/utils'
import {
  useAcceptCampaignMutation,
  useCompleteStepMutation,
  useCounterOfferMutation,
  useGetCampaignDetailQuery,
  useQuoteRateMutation,
  useRejectCampaignMutation,
  useSendCollaborationMessageMutation,
  useSubmitMilestoneMutation,
  useUpdateCollaborationMutation,
  type Collaboration,
} from '@/redux/collaborationsApi'
import {
  DEAL_TYPE_LABELS,
  complianceItems,
  dealStatusLine,
  dealTypeOf,
  stageOf,
  workSteps,
  type DealLike,
} from '@/lib/dealStages'
import DealStageBar from '@/components/deal/DealStageBar'
import ComplianceCard from '@/components/deal/ComplianceCard'
import { GiftingBonusCard } from '@/components/deal/GiftingBonusCard'
import { AgreementCard } from '@/components/deal/AgreementCard'
import { NilGoCard } from '@/components/deal/NilGoCard'
import { ConceptStep } from '@/components/deal/ConceptStep'
import { ContentStep } from '@/components/deal/ContentStep'
import { CodesStep, LiveLinksStep } from '@/components/deal/LiveStep'
import { StepCard } from '@/components/deal/StepCard'
import { CheckInPrompt, EventsCard, MoreTime } from '@/components/deal/StepExtras'
import {
  AppearanceDetails,
  Card,
  CheckIn,
  Eyebrow,
  LinksForm,
  ShippingDetails,
  day,
  fieldCls,
  money,
  primaryCls,
} from '@/components/deal/ui'

const BRAND_STEPS = ['product_shipped', 'prepare_appearance', 'brand_confirms', 'brand_approves']
const slugOf = (m: any): string => m.slug ?? m.type_slug ?? ''
const isDone = (m: any) => m.status === 'approved' || m.status === 'completed'
const latestOf = (m: any) =>
  [...(m?.submissions ?? [])].sort((a, b) => (b.version ?? 0) - (a.version ?? 0))[0] ?? null

/** What a step is called on the athlete's side */
const STEP_TITLE: Record<string, string> = {
  submit_creative_concept: 'Creative concept',
  submit_video: 'Content review',
  post_content: 'Post it',
  create_draft: 'Draft for review',
  product_shipped: 'Product on its way',
  confirm_received: 'Product received',
  prepare_appearance: 'Appearance details',
  check_in: 'Check in',
  brand_confirms: 'Brand confirms',
  brand_approves: 'Final approval',
  collaboration_live: 'Go live',
  content_live: 'Go live',
  license_content: 'Ad codes',
}

/**
 * One screen for every deal: the five-stage bar, then the stages themselves, each a card that
 * opens to its form or its result. Mirrors the brand's workspace step for step, so what the
 * athlete sends is what the brand reviews.
 */
const DealScreen: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: deal, isLoading, refetch } = useGetCampaignDetailQuery(id, { skip: !id })
  const [accept] = useAcceptCampaignMutation()
  const [reject] = useRejectCampaignMutation()
  const [counter] = useCounterOfferMutation()
  const [quote] = useQuoteRateMutation()
  const [submitMilestone] = useSubmitMilestoneMutation()
  const [completeStep] = useCompleteStepMutation()
  const [updateCollaboration] = useUpdateCollaborationMutation()
  const [sendMessage] = useSendCollaborationMessageMutation()
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState<string | null>(null)
  const [showDetails, setShowDetails] = useState(false)
  const [showChat, setShowChat] = useState(false)
  const [chat, setChat] = useState('')
  const [messages, setMessages] = useState<any[] | null>(null)

  // Re-open on what needs attention after something changes, unless the athlete picked a step
  useEffect(() => {
    setOpen(null)
  }, [(deal as any)?.updatedAt, deal?.status])

  if (isLoading || !deal) {
    return (
      <p className="mx-auto max-w-md text-sm text-muted-foreground">
        {isLoading ? 'Loading…' : 'Deal not found.'}
      </p>
    )
  }

  const d = deal as Collaboration & DealLike & Record<string, any>
  const s = stageOf(d)
  const type = dealTypeOf(d)
  const brand = d.brand?.name ?? 'The brand'
  const milestones: any[] = d.milestones ?? []
  const steps = workSteps(d) as any[]
  const current = s.currentStep as any
  const agreed = d.status === 'accepted'
  const compliance = d.compliance
  const nilBlocks = !!compliance?.required && agreed && compliance.state !== 'cleared'
  const licenseMilestone = milestones.find((m) => slugOf(m) === 'license_content')
  const liveMilestone = milestones.find((m) =>
    ['collaboration_live', 'content_live'].includes(slugOf(m)),
  )
  const managerActions = (d.timeline ?? [])
    .filter((m: any) => m.metadata?.event === 'manager_action')
    .slice(-5)
    .reverse()

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(true)
    try {
      await fn()
      await refetch()
      if (label) toast(label)
    } catch (err: any) {
      toast.error('That didn’t go through', {
        description: err?.data?.error ?? err?.response?.data?.error ?? err?.message,
      })
      throw err
    } finally {
      setBusy(false)
    }
  }

  const submit = (milestoneId: string, submissionData: Record<string, unknown>, label: string) =>
    run(label, async () => {
      await submitMilestone({
        campaignId: id,
        milestoneId: milestoneId as any,
        submissionData,
      }).unwrap()
      track('deal_work_submitted', { dealId: id })
    })
  const submitLinks = (milestoneId: string, kind: string, links: string[], note: string) =>
    submit(
      milestoneId,
      { content_description: kind, content_urls: { links }, notes: note || null },
      'Sent to the brand',
    )
  const complete = (milestoneId: string, body: Record<string, unknown> = {}) =>
    run('Step done', () => completeStep({ campaignId: id, milestoneId, body }).unwrap())

  const loadMessages = async () => {
    const res = await athleteApi.get(`/athlete/collaborations/${id}/messages`)
    setMessages(res.data?.messages ?? res.data ?? [])
  }

  // ── Which step is open by default: the one that needs the athlete, else the current one ──
  const stepState = (m: any): 'done' | 'needs_you' | 'with_brand' | 'upcoming' => {
    if (isDone(m)) return 'done'
    if (!agreed) return 'upcoming'
    const latest = latestOf(m)
    const slug = slugOf(m)
    if (slug === 'license_content') {
      return m.metadata?.step === 'ad_code_submission' || liveDone ? 'needs_you' : 'upcoming'
    }
    if (latest?.status === 'pending_review' || m.status === 'submitted') return 'with_brand'
    if (current?.id === m.id) return BRAND_STEPS.includes(slug) ? 'with_brand' : 'needs_you'
    return 'upcoming'
  }
  const liveDone = !!liveMilestone && (isDone(liveMilestone) || (d.postLinks?.length ?? 0) > 0)
  const allSteps = [
    ...steps,
    ...(licenseMilestone && d.licensingEnabled ? [licenseMilestone] : []),
  ]
  const defaultOpen =
    (nilBlocks ? null : allSteps.find((m) => stepState(m) === 'needs_you')?.id) ??
    allSteps.find((m) => stepState(m) === 'with_brand')?.id ??
    null
  const openId = open ?? defaultOpen

  const stepStatus = (m: any, st: ReturnType<typeof stepState>) => {
    const latest = latestOf(m)
    if (st === 'done') return 'Done'
    if (st === 'needs_you')
      return latest?.status === 'revision_requested' ? 'Changes asked' : 'Your move'
    if (st === 'with_brand')
      return BRAND_STEPS.includes(slugOf(m)) ? `${brand}'s step` : 'In review'
    return undefined
  }

  // ── The form or result inside a step ─────────────────────────────────────
  const stepBody = (m: any, st: ReturnType<typeof stepState>): React.ReactNode => {
    const slug = slugOf(m)
    if (!agreed) return null
    switch (slug) {
      case 'submit_creative_concept':
        return (
          <ConceptStep
            dealId={id}
            milestone={m}
            brandName={brand}
            busy={busy}
            onSubmit={(text) =>
              submit(
                m.id,
                {
                  content_description: JSON.stringify({
                    fields: [
                      { label: 'Creative Concept', value: text, type: 'textarea', order: 1 },
                    ],
                  }),
                  notes: null,
                  metadata: null,
                },
                'Concept sent',
              )
            }
          />
        )
      case 'submit_video':
        return (
          <ContentStep
            dealId={id}
            milestone={m}
            deliverables={(d.structuredDeliverables ?? []) as any}
            brandName={brand}
            busy={busy}
            onSubmit={(payload) => submit(m.id, payload, 'Sent for review')}
          />
        )
      case 'post_content':
      case 'create_draft': {
        const latest = latestOf(m)
        if (st !== 'needs_you') {
          const links: string[] = latest?.contentUrls?.links ?? []
          return links.length ? (
            <ul className="mt-3 space-y-1 text-sm">
              {links.map((l) => (
                <li key={l}>
                  <a
                    href={l}
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-[#3F7A0A] underline"
                  >
                    {l}
                  </a>
                </li>
              ))}
            </ul>
          ) : null
        }
        return slug === 'post_content' ? (
          <LinksForm
            label="Paste the link to your post. Remember #ad."
            placeholder="https://www.instagram.com/p/…"
            button="Send to the brand"
            busy={busy}
            onSubmit={(links, note) => submitLinks(m.id, 'Post', links, note)}
          />
        ) : (
          <LinksForm
            label="Share a link to your draft (Drive, Dropbox, or an unlisted post)."
            placeholder="https://…"
            button="Send draft for review"
            note
            busy={busy}
            onSubmit={(links, note) => submitLinks(m.id, 'Draft', links, note)}
          />
        )
      }
      case 'product_shipped':
        return st === 'with_brand' ? (
          <p className="mt-3 text-sm text-muted-foreground">
            {brand} is packing it up. You'll see the tracking here.
          </p>
        ) : (
          <ShippingDetails milestones={milestones} />
        )
      case 'confirm_received':
        return (
          <>
            <ShippingDetails milestones={milestones} />
            {st === 'needs_you' ? (
              <button
                type="button"
                className={`mt-3 ${primaryCls}`}
                disabled={busy}
                onClick={() => complete(m.id)}
              >
                Confirm I got it
              </button>
            ) : null}
          </>
        )
      case 'prepare_appearance':
        return st === 'with_brand' ? (
          <p className="mt-3 text-sm text-muted-foreground">
            {brand} is adding where and when. You'll see it here.
          </p>
        ) : (
          <AppearanceDetails milestones={milestones} />
        )
      case 'check_in':
        return (
          <>
            <AppearanceDetails milestones={milestones} />
            {st === 'needs_you' ? (
              <CheckIn
                busy={busy}
                onSubmit={(note) => submitLinks(m.id, 'Checked in', [], note)}
              />
            ) : null}
          </>
        )
      case 'brand_confirms':
      case 'brand_approves':
        return st === 'done' ? null : (
          <p className="mt-3 text-sm text-muted-foreground">
            {brand} signs this off on their side. Nothing for you to do.
          </p>
        )
      case 'collaboration_live':
      case 'content_live':
        return (
          <LiveLinksStep
            postLinks={d.postLinks ?? []}
            done={isDone(m)}
            busy={busy}
            onSubmit={(links) =>
              slug === 'content_live'
                ? complete(m.id, { postLinks: links })
                : run('It’s live', () =>
                    updateCollaboration({
                      campaignId: id,
                      updates: { livePostLinks: links },
                    }).unwrap(),
                  )
            }
          />
        )
      case 'license_content':
        return (
          <CodesStep
            dealId={id}
            milestone={m}
            brandName={brand}
            licenseDays={m.metadata?.term_days ?? d.booking?.licenseDays ?? null}
          />
        )
      default:
        return st === 'needs_you' ? (
          <button
            type="button"
            className={`mt-3 ${primaryCls}`}
            disabled={busy}
            onClick={() => complete(m.id)}
          >
            Mark as done
          </button>
        ) : null
    }
  }

  return (
    <div className="mx-auto w-full max-w-md pb-10">
      <button
        type="button"
        onClick={() => navigate('/deals')}
        className="-ml-1 mb-3 inline-flex items-center gap-1 text-sm font-medium text-foreground hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> Deals
      </button>

      {/* Header */}
      <div className="flex items-center gap-3">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-muted text-sm font-bold text-foreground">
          {d.brand?.logo ? (
            <img src={d.brand.logo} alt="" className="h-full w-full object-cover" />
          ) : (
            (d.brand?.name ?? '?').slice(0, 2).toUpperCase()
          )}
        </span>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold truncate text-foreground">
            {brand} · {d.title}
          </h1>
          <p className="text-sm text-muted-foreground">
            {DEAL_TYPE_LABELS[type]} · {money(d.paymentAmount)}
            {d.contentDueDate ? ` · due ${day(d.contentDueDate)}` : ''}
          </p>
        </div>
      </div>

      <DealStageBar state={s} className="mt-5" />
      <p
        className={cn(
          'mt-3 text-sm',
          s.waitingOn === 'athlete' || nilBlocks
            ? 'font-semibold text-[#2F5E06]'
            : 'text-muted-foreground',
        )}
      >
        {nilBlocks ? 'Your move: file your NIL Go report' : dealStatusLine(d, s)}
      </p>

      <div className="mt-4 space-y-3">
        {/* 1. Agreement */}
        {!agreed ? (
          <AgreementCard
            deal={d}
            busy={busy}
            onAccept={() =>
              run('Offer accepted', async () => {
                await accept(id).unwrap()
                track('deal_accepted', { dealId: id })
              })
            }
            onPass={() =>
              run('Offer declined', async () => {
                await reject(id).unwrap()
                track('deal_declined', { dealId: id })
              })
            }
            onCounter={(amount, note) =>
              run('Counter sent. The brand can accept, counter or pass.', async () => {
                await counter({ campaignId: id, amount, note: note || undefined }).unwrap()
                track('deal_countered', { dealId: id })
              })
            }
            onQuote={(amount, note) =>
              run('Rate sent. The brand can accept, counter or pass.', () =>
                quote({ campaignId: id, amount, note: note || undefined }).unwrap(),
              )
            }
          />
        ) : null}

        {/* 2. NIL Go, on deals that need it */}
        {agreed && compliance?.required ? (
          <NilGoCard
            deal={deal}
            compliance={compliance}
            defaultOpen={nilBlocks && compliance.inProgress}
          />
        ) : null}

        {/* Shoots and store visits the brand scheduled */}
        {agreed ? <EventsCard dealId={id} brandName={brand} /> : null}

        {/* Gifting: bonuses as views come in, and the paid usage window */}
        {d.performance?.terms ? <GiftingBonusCard performance={d.performance} /> : null}

        {/* 3. The work, step by step */}
        {allSteps.length ? (
          <div className="space-y-2">
            <Eyebrow>
              {s.stage === 'work'
                ? `Step ${Math.min(s.stepsDone + 1, s.stepsTotal)} of ${s.stepsTotal}`
                : 'The work'}
            </Eyebrow>
            {allSteps.map((m, i) => {
              const st = stepState(m)
              const inner = stepBody(m, st)
              const mine = st === 'needs_you' && !BRAND_STEPS.includes(slugOf(m))
              const body =
                inner || mine ? (
                  <>
                    {mine ? (
                      <CheckInPrompt
                        dealId={id}
                        milestoneId={m.id}
                        checkIn={m.checkIn}
                        brandName={brand}
                      />
                    ) : null}
                    {inner}
                    {mine && slugOf(m) !== 'license_content' ? (
                      <MoreTime
                        dealId={id}
                        milestoneId={m.id}
                        dueDate={m.dueDate}
                        proposal={m.dateProposal}
                        brandName={brand}
                      />
                    ) : null}
                  </>
                ) : null
              return (
                <StepCard
                  key={m.id}
                  index={i + 1}
                  title={STEP_TITLE[slugOf(m)] ?? m.name}
                  state={st}
                  status={stepStatus(m, st)}
                  date={
                    st === 'done' && m.completedAt
                      ? `Done ${day(m.completedAt)}`
                      : m.dueDate
                        ? `Due ${day(m.dueDate)}`
                        : undefined
                  }
                  open={openId === m.id}
                  onToggle={() => setOpen(openId === m.id ? '' : m.id)}
                >
                  {body}
                </StepCard>
              )
            })}
          </div>
        ) : null}

        {/* 4. Done and paid */}
        {s.stage === 'complete' || s.stage === 'paid' ? (
          <Card tone="lime">
            <Eyebrow tone="lime">{s.stage === 'paid' ? 'Paid' : 'Deal complete'}</Eyebrow>
            <p className="mt-1 font-bold text-foreground">
              {d.paymentAmount && d.paymentAmount > 0
                ? s.stage === 'paid'
                  ? `${money(d.paymentAmount)} paid to your bank`
                  : `${money(d.paymentAmount)} on its way`
                : 'Nice work'}
            </p>
            <p className="text-sm text-muted-foreground">
              {d.paymentAmount && d.paymentAmount > 0
                ? 'The brand pays Athios’s fee on top. You get the full amount, through Stripe.'
                : 'The brand approved it.'}
            </p>
          </Card>
        ) : null}

        {/* What a manager did on this deal, so nothing happens behind the athlete's back */}
        {managerActions.length ? (
          <Card tone="blue">
            <Eyebrow>Your manager on this deal</Eyebrow>
            <ul className="mt-2 space-y-1.5 text-sm">
              {managerActions.map((m: any) => (
                <li key={m.id} className="flex items-start justify-between gap-3">
                  <span className="text-foreground">{m.body}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {day(m.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-muted-foreground">
              Change what your manager can do for you under Settings → Managers.
            </p>
          </Card>
        ) : null}

        <ComplianceCard items={complianceItems(d)} />

        {/* The brief and terms */}
        <button
          type="button"
          onClick={() => setShowDetails((v) => !v)}
          className="flex w-full items-center justify-between rounded-2xl border border-border bg-card px-4 py-3 text-left text-sm font-bold text-foreground shadow-sm"
        >
          The brief and terms
          {showDetails ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
        {showDetails ? (
          <Card className="space-y-3 text-sm">
            {d.brief ? <p className="whitespace-pre-wrap text-foreground">{d.brief}</p> : null}
            {d.instructions ? (
              <p className="whitespace-pre-wrap text-muted-foreground">{d.instructions}</p>
            ) : null}
            {Array.isArray(d.structuredDeliverables) && d.structuredDeliverables.length ? (
              <ul className="space-y-1">
                {d.structuredDeliverables.map((x: any, i: number) => (
                  <li key={x.id ?? i} className="text-foreground">
                    {x.platform} {x.format}
                    {x.notes ? <span className="text-muted-foreground"> · {x.notes}</span> : null}
                  </li>
                ))}
              </ul>
            ) : null}
            <dl className="grid grid-cols-2 gap-y-1 text-muted-foreground">
              <dt>Pay</dt>
              <dd className="text-right font-semibold text-foreground">
                {money(d.paymentAmount)}
              </dd>
              {d.contentDueDate ? (
                <>
                  <dt>Due</dt>
                  <dd className="text-right text-foreground">{day(d.contentDueDate, true)}</dd>
                </>
              ) : null}
              {type !== 'appearance' ? (
                <>
                  <dt>Ad usage</dt>
                  <dd className="text-right text-foreground">
                    {30 + (d.booking?.licenseDays ?? 0)} days included
                  </dd>
                </>
              ) : null}
            </dl>
          </Card>
        ) : null}

        {/* Conversation */}
        <button
          type="button"
          onClick={() => {
            setShowChat((v) => !v)
            if (!messages) loadMessages().catch(() => setMessages([]))
          }}
          className="flex w-full items-center justify-between rounded-2xl border border-border bg-card px-4 py-3 text-left text-sm font-bold text-foreground shadow-sm"
        >
          <span className="inline-flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-muted-foreground" /> Message {brand}
          </span>
          {showChat ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
        {showChat ? (
          <Card className="space-y-3">
            <ul className="max-h-72 space-y-2 overflow-y-auto text-sm">
              {(messages ?? []).filter((m: any) => (m.messageType ?? m.message_type) === 'user')
                .length === 0 ? (
                <li className="text-muted-foreground">No messages yet.</li>
              ) : null}
              {(messages ?? [])
                .filter((m: any) => (m.messageType ?? m.message_type) === 'user')
                .map((m: any) => (
                  <li
                    key={m.id}
                    className={cn(
                      'rounded-xl px-3 py-2',
                      m.isFromMe || m.is_from_me
                        ? 'ml-6 bg-[#E8F9D2] text-foreground'
                        : 'mr-6 bg-muted text-foreground',
                    )}
                  >
                    {m.body ?? m.message}
                  </li>
                ))}
            </ul>
            <div className="flex gap-2">
              <input
                value={chat}
                onChange={(e) => setChat(e.target.value)}
                placeholder="Write a message"
                className={fieldCls}
              />
              <button
                type="button"
                className="shrink-0 rounded-xl bg-[#C0FD72] px-4 text-sm font-semibold text-[#0F172A] disabled:opacity-50"
                disabled={busy || !chat.trim()}
                onClick={() =>
                  run('Sent', async () => {
                    await sendMessage({ campaignId: id, message: chat.trim() }).unwrap()
                    setChat('')
                    await loadMessages()
                  })
                }
              >
                Send
              </button>
            </div>
          </Card>
        ) : null}
      </div>
    </div>
  )
}

export default DealScreen
